import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import './SearchDropdown.css';

const SearchDropdown = ({ isOpen, onClose, searchTerm, setSearchTerm }) => {
  const [selectedDuration, setSelectedDuration] = useState('');
  const [priceRange, setPriceRange] = useState([20000, 500000]);
  const [includeFlights, setIncludeFlights] = useState(false);
  const [draggedHandle, setDraggedHandle] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleDurationChange = (duration) => {
    setSelectedDuration(duration);
  };

  const handleMinClick = () => {
    setPriceRange([20000, priceRange[1]]);
  };

  const handleMaxClick = () => {
    setPriceRange([priceRange[0], 500000]);
  };

  const setMinPrice = () => {
    setPriceRange([20000, priceRange[1]]);
  };

  const setMaxPrice = () => {
    setPriceRange([priceRange[0], 500000]); // 5L
  };

  const formatPriceDisplay = (price) => {
    if (price >= 100000) {
      return `${Math.round(price / 100000)}L`;
    }
    return `${Math.round(price / 1000)}K`;
  };

  const clearAllFilters = () => {
    setSelectedDuration('');
    setPriceRange([20000, 500000]);
    setIncludeFlights(false);
  };

  // Mouse event handlers for dragging handles
  const handleMouseDown = (handle, event) => {
    event.preventDefault();
    setDraggedHandle(handle);
  };

  const handleMouseMove = useCallback((event) => {
    if (!draggedHandle) return;

    const slider = event.currentTarget;
    if (!slider) return;
    
    const rect = slider.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const rawValue = (percentage / 100) * 500000;
    
    // Round to nearest 1000
    const value = Math.round(rawValue / 1000) * 1000;

    if (draggedHandle === 'min' && value < priceRange[1] - 10000) {
      const newMin = Math.max(20000, value);
      if (newMin !== priceRange[0]) { // Only update if changed
        setPriceRange([newMin, priceRange[1]]);
      }
    } else if (draggedHandle === 'max' && value > priceRange[0] + 10000) {
      const newMax = Math.min(500000, value);
      if (newMax !== priceRange[1]) { // Only update if changed
        setPriceRange([priceRange[0], newMax]);
      }
    }
  }, [draggedHandle, priceRange]);

  const handleMouseUp = useCallback(() => {
    setDraggedHandle(null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setDraggedHandle(null);
  }, []);

  const handleSearch = () => {
    // Build search parameters
    const searchParams = new URLSearchParams();
    
    if (searchTerm) {
      searchParams.set('q', searchTerm);
    }
    
    if (selectedDuration) {
      searchParams.set('duration', selectedDuration);
    }
    
    searchParams.set('minPrice', priceRange[0].toString());
    searchParams.set('maxPrice', priceRange[1].toString());
    
    if (includeFlights) {
      searchParams.set('includeFlights', 'true');
    }

    // Navigate to search results page
    navigate(`/search?${searchParams.toString()}`);
    
    // Close the dropdown
    onClose();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="search-dropdown" ref={dropdownRef}>
      <div className="search-dropdown-content">
        <div className="dropdown-body">
          {/* Trip Duration Filter */}
          <div className="filter-section">
            <h3>Trip Duration</h3>
            <div className="duration-buttons">
              {[
                'Upto 1 Day',
                '2 to 3 days',
                '3 to 5 days',
                '5 to 7 days',
                '7+ Days'
              ].map((duration) => (
                <button
                  key={duration}
                  className={`filter-btn ${selectedDuration === duration ? 'active' : ''}`}
                  onClick={() => handleDurationChange(duration)}
                >
                  {duration}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="filter-section">
            <h3>Price Range</h3>
            <div className="price-range-container">
              <div className="price-display">
                <button className="price-btn min-btn" onClick={setMinPrice}>
                  Min: {formatPriceDisplay(priceRange[0])}
                </button>
                <button className="price-btn max-btn" onClick={setMaxPrice}>
                  Max: {formatPriceDisplay(priceRange[1])}
                </button>
              </div>
              <div className="range-slider-wrapper">
                <div className="range-track">
                  <div 
                    className="range-progress" 
                    style={{
                      left: `${(priceRange[0] / 500000) * 100}%`,
                      width: `${((priceRange[1] - priceRange[0]) / 500000) * 100}%`
                    }}
                  ></div>
                </div>
                
                {/* Interactive slider area */}
                <div 
                  className="slider-area"
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Min Handle */}
                  <div 
                    className="range-handle range-handle-min"
                    style={{ left: `${(priceRange[0] / 500000) * 100}%` }}
                    onMouseDown={(e) => handleMouseDown('min', e)}
                  >
                    {formatPriceDisplay(priceRange[0])}
                  </div>

                  {/* Max Handle */}
                  <div 
                    className="range-handle range-handle-max"
                    style={{ left: `${(priceRange[1] / 500000) * 100}%` }}
                    onMouseDown={(e) => handleMouseDown('max', e)}
                  >
                    {formatPriceDisplay(priceRange[1])}
                  </div>
                </div>
              </div>
              <div className="price-inputs">
                <div className="price-input-group">
                  <span>Min</span>
                  <span>INR {priceRange[0].toLocaleString()}</span>
                </div>
                <div className="price-input-group">
                  <span>Max</span>
                  <span>INR {priceRange[1].toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Flights Inclusion */}
          <div className="filter-section">
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="includeFlights"
                checked={includeFlights}
                onChange={(e) => setIncludeFlights(e.target.checked)}
                className="checkbox"
              />
              <label htmlFor="includeFlights">I want Flights to be included</label>
            </div>
          </div>

          {/* Search Button */}
          <div className="search-action">
            <button className="clear-all-btn" onClick={clearAllFilters}>
              Clear All
            </button>
            <button className="search-products-btn" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchDropdown;
